import React, { useState, useEffect, useRef, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Spinner from "../../Spinner/spinner";
import { Button, Alert, Badge } from "reactstrap";
import { Helmet } from "react-helmet";
import { localeEs } from "../../locale/ag-grid-es";
import BreadCrum from "../../breadCrum/breadCrum";
import withTokenCheck from "../../withTokenCheck";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const AppCargaInformacion = ({ token }) => {
    // Estados para los dos archivos Excel
    const [dataValidacion, setDataValidacion] = useState([]);
    const [dataFacturacion, setDataFacturacion] = useState([]);
    const [dataCombinada, setDataCombinada] = useState([]);

    const fileValidacionRef = useRef(null);
    const fileFacturacionRef = useRef(null);
    const gridApi = useRef(null);

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: "", message: "" });
    const [hasChanges, setHasChanges] = useState(false);
    const [archivosCargados, setArchivosCargados] = useState({
        validacion: false,
        facturacion: false
    });

    const etiquetas = [
        "Inicio Lossman",
        "Cambios De Evento",
        "Cargue de información",
    ];

    const mostrarAlerta = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => {
            setAlert({ show: false, type: "", message: "" });
        }, 5000);
    };

    const combinarDatos = useCallback(() => {
        const dataCombinada = [];
        const mapValidacion = new Map();
        const mapFacturacion = new Map();

        // Crear mapas con numero de caso como clave
        dataValidacion.forEach(item => {
            mapValidacion.set(item.numeroCaso, item);
        });

        dataFacturacion.forEach(item => {
            mapFacturacion.set(item.numeroCaso, item);
        });

        // Obtener todos los números de caso únicos
        const todosCasos = new Set([
            ...Array.from(mapValidacion.keys()),
            ...Array.from(mapFacturacion.keys())
        ]);

        // Combinar información
        todosCasos.forEach(numeroCaso => {
            const itemValidacion = mapValidacion.get(numeroCaso) || {};
            const itemFacturacion = mapFacturacion.get(numeroCaso) || {};

            dataCombinada.push({
                numeroCaso: numeroCaso,
                numeroValidacion: itemValidacion.numeroValidacion || null,
                numeroFactura: itemFacturacion.numeroFactura || null,
                fechaFacturacion: itemFacturacion.fechaFacturacion || null,
                origen: {
                    validacion: mapValidacion.has(numeroCaso),
                    facturacion: mapFacturacion.has(numeroCaso)
                }
            });
        });

        setDataCombinada(dataCombinada);
        setHasChanges(true);
    }, [dataValidacion, dataFacturacion]);

    // Combinar datos cuando ambos archivos estén cargados
    useEffect(() => {
        if (dataValidacion.length > 0 || dataFacturacion.length > 0) {
            combinarDatos();
        }
    }, [dataValidacion, dataFacturacion, combinarDatos]);

    // Procesar Excel de Validación (Numero Caso, Numero Validación)
    const handleFileValidacion = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!validarArchivoExcel(file)) return;

        setLoading(true);
        const reader = new FileReader();

        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });

                if (jsonData.length < 2) {
                    mostrarAlerta("warning", "El archivo de validación está vacío");
                    setLoading(false);
                    return;
                }

                const rows = jsonData.slice(1);
                const processedData = rows.map((row, index) => ({
                    id: `val_${index + 1}`,
                    numeroCaso: String(row[0] || "").trim(),
                    numeroValidacion: String(row[1] || "").trim(),
                })).filter(row => row.numeroCaso);

                if (processedData.length === 0) {
                    mostrarAlerta("warning", "No se encontraron datos válidos en el archivo de validación");
                    setLoading(false);
                    return;
                }

                setDataValidacion(processedData);
                setArchivosCargados(prev => ({ ...prev, validacion: true }));
                mostrarAlerta("success", `Archivo de validación: ${processedData.length} registros cargados`);
                setLoading(false);

            } catch (error) {
                console.error("Error al procesar archivo de validación:", error);
                mostrarAlerta("danger", "Error al procesar el archivo de validación");
                setLoading(false);
            }
        };

        reader.onerror = () => {
            mostrarAlerta("danger", "Error al leer el archivo de validación");
            setLoading(false);
        };

        reader.readAsBinaryString(file);
    };

    // Procesar Excel de Facturación (Numero Caso, Numero Factura, Fecha Facturación)
    const handleFileFacturacion = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!validarArchivoExcel(file)) return;

        setLoading(true);
        const reader = new FileReader();

        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });

                if (jsonData.length < 2) {
                    mostrarAlerta("warning", "El archivo de facturación está vacío");
                    setLoading(false);
                    return;
                }

                const rows = jsonData.slice(1);
                const processedData = rows.map((row, index) => ({
                    id: `fact_${index + 1}`,
                    numeroCaso: String(row[0] || "").trim(),
                    numeroFactura: String(row[1] || "").trim(),
                    fechaFacturacion: procesarFecha(row[2]),
                })).filter(row => row.numeroCaso);

                if (processedData.length === 0) {
                    mostrarAlerta("warning", "No se encontraron datos válidos en el archivo de facturación");
                    setLoading(false);
                    return;
                }

                setDataFacturacion(processedData);
                setArchivosCargados(prev => ({ ...prev, facturacion: true }));
                mostrarAlerta("success", `Archivo de facturación: ${processedData.length} registros cargados`);
                setLoading(false);

            } catch (error) {
                console.error("Error al procesar archivo de facturación:", error);
                mostrarAlerta("danger", "Error al procesar el archivo de facturación");
                setLoading(false);
            }
        };

        reader.onerror = () => {
            mostrarAlerta("danger", "Error al leer el archivo de facturación");
            setLoading(false);
        };

        reader.readAsBinaryString(file);
    };

    const validarArchivoExcel = (file) => {
        const validExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            mostrarAlerta("danger", "Por favor, selecciona un archivo Excel válido (.xlsx o .xls)");
            return false;
        }
        return true;
    };

    const procesarFecha = (fecha) => {
        if (!fecha) return null;

        // Si es un número serial de Excel
        if (typeof fecha === 'number') {
            const date = XLSX.SSF.parse_date_code(fecha);
            return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
        }

        // Si ya es una fecha en formato texto
        return String(fecha).trim();
    };

    
    const descargarArchivoErrores = (contenido, nombreArchivo = 'errores_carga_informacion.txt') => {
        const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = nombreArchivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleGuardarDatos = async () => {
        if (!hasChanges || dataCombinada.length === 0) {
            mostrarAlerta("warning", "No hay datos para guardar");
            return;
        }

        // Validar que al menos un archivo esté cargado
        if (!archivosCargados.validacion && !archivosCargados.facturacion) {
            mostrarAlerta("warning", "Debes cargar al menos un archivo antes de guardar");
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                "formulario/facturacion/cargar",
                {
                    registros: dataCombinada,
                    archivosCargados: archivosCargados
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                }
            );

            // Construir el HTML para mostrar los resultados detallados
            const resultados = response.data.resultados;
            const archivoErrores = response.data.archivoErrores;
            let htmlContent = '<div style="text-align: left;">';

            // Información de validación
            if (archivosCargados.validacion) {
                htmlContent += `
                    <div style="margin-bottom: 15px;">
                        <strong>📋 Validación:</strong>
                        <ul style="margin: 5px 0;">
                            <li>Intentados: ${resultados.validacion.intentados}</li>
                            <li>Exitosos: <span style="color: green;">${resultados.validacion.exitosos}</span></li>
                            <li>Fallidos: <span style="color: red;">${resultados.validacion.fallidos}</span></li>
                        </ul>
                    </div>
                `;
            }

            // Información de facturación
            if (archivosCargados.facturacion) {
                htmlContent += `
                    <div style="margin-bottom: 15px;">
                        <strong>💰 Facturación:</strong>
                        <ul style="margin: 5px 0;">
                            <li>Intentados: ${resultados.facturacion.intentados}</li>
                            <li>Exitosos: <span style="color: green;">${resultados.facturacion.exitosos}</span></li>
                            <li>Fallidos: <span style="color: red;">${resultados.facturacion.fallidos}</span></li>
                        </ul>
                    </div>
                `;
            }

            // Total de registros
            htmlContent += `
                <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #ddd;">
                    <strong>Total de registros procesados: ${dataCombinada.length}</strong>
                </div>
            `;

            // Mostrar errores si los hay
            const totalErrores = (resultados.validacion.errores?.length || 0) +
                (resultados.facturacion.errores?.length || 0);

            if (totalErrores > 0) {
                htmlContent += `
                    <div style="margin-top: 10px; color: #d32f2f;">
                        ⚠️ Se encontraron ${totalErrores} errores. Revisa la consola para más detalles.
                    </div>
                `;
                console.log('Errores de validación:', resultados.validacion.errores);
                console.log('Errores de facturación:', resultados.facturacion.errores);
            }

            htmlContent += '</div>';

            setLoading(false);

            const result = await Swal.fire({
                icon: "success",
                title: "¡Datos Guardados!",
                html: htmlContent,
                showCloseButton: true,
                confirmButtonText: "Aceptar",
                showDenyButton: archivoErrores ? true : false,
                denyButtonText: archivoErrores ? '📥 Descargar Reporte de Errores' : undefined,
                denyButtonColor: '#f44336',
                width: 600,
            });

            if (result.isDenied && archivoErrores) {
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                descargarArchivoErrores(archivoErrores, `errores_carga_${timestamp}.txt`);

                Swal.fire({
                    icon: 'info',
                    title: 'Reporte Descargado',
                    text: 'El reporte de errores se ha descargado correctamente.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }

            setHasChanges(false);
            limpiarDatosSilencioso();
            mostrarAlerta("success", response.data.message);

        } catch (error) {

            const errorData = error.response?.data;const 
            archivoErrores = errorData?.archivoErrores;
            let htmlError = '<div style="text-align: left;">';

            if (errorData?.resultados) {
                const resultados = errorData.resultados;

                htmlError += `<p><strong>Detalles del error:</strong></p>`;

                // Mostrar errores de validación
                if (resultados.validacion?.errores?.length > 0) {
                    htmlError += `<div style="margin: 10px 0;"><strong>Errores de Validación:</strong><ul>`;
                    resultados.validacion.errores.slice(0, 5).forEach(err => {
                        htmlError += `<li>Caso ${err.caso}: ${err.error}</li>`;
                    });
                    if (resultados.validacion.errores.length > 5) {
                        htmlError += `<li>... y ${resultados.validacion.errores.length - 5} errores más</li>`;
                    }
                    htmlError += `</ul></div>`;
                }

                // Mostrar errores de facturación
                if (resultados.facturacion?.errores?.length > 0) {
                    htmlError += `<div style="margin: 10px 0;"><strong>Errores de Facturación:</strong><ul>`;
                    resultados.facturacion.errores.slice(0, 5).forEach(err => {
                        htmlError += `<li>Caso ${err.caso}: ${err.error}</li>`;
                    });
                    if (resultados.facturacion.errores.length > 5) {
                        htmlError += `<li>... y ${resultados.facturacion.errores.length - 5} errores más</li>`;
                    }
                    htmlError += `</ul></div>`;
                }
            } else {
                htmlError += `<p>${errorData?.message || error.message || "Error desconocido"}</p>`;
            }

            htmlError += '</div>';

            setLoading(false);

            const result = await Swal.fire({
                icon: "error",
                title: "Error al Guardar",
                html: htmlError,
                showCloseButton: true,
                confirmButtonText: "Entendido",
                showDenyButton: archivoErrores ? true : false,
                denyButtonText: archivoErrores ? '📥 Descargar Reporte Completo de Errores' : undefined,
                denyButtonColor: '#f44336',
                width: 600,
            });

            // Si el usuario hace clic en "Descargar Reporte Completo de Errores"
            if (result.isDenied && archivoErrores) {
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                descargarArchivoErrores(archivoErrores, `errores_criticos_${timestamp}.txt`);

                Swal.fire({
                    icon: 'info',
                    title: 'Reporte Descargado',
                    text: 'El reporte completo de errores se ha descargado correctamente.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }

            limpiarDatosSilencioso();
            mostrarAlerta("danger", errorData?.message || "Error al guardar los datos");
        } finally {
            setLoading(false);
        }
    };

    const limpiarDatosSilencioso = () => {
        setDataValidacion([]);
        setDataFacturacion([]);
        setDataCombinada([]);
        setHasChanges(false);
        setArchivosCargados({ validacion: false, facturacion: false });

        if (fileValidacionRef.current) fileValidacionRef.current.value = "";
        if (fileFacturacionRef.current) fileFacturacionRef.current.value = "";
    };


    const handleLimpiarDatos = () => {
        if (window.confirm("¿Estás seguro de limpiar todos los datos cargados?")) {
            setDataValidacion([]);
            setDataFacturacion([]);
            setDataCombinada([]);
            setHasChanges(false);
            setArchivosCargados({ validacion: false, facturacion: false });

            if (fileValidacionRef.current) fileValidacionRef.current.value = "";
            if (fileFacturacionRef.current) fileFacturacionRef.current.value = "";

            mostrarAlerta("info", "Datos limpiados correctamente");
        }
    };


    const onGridReady = (params) => {
        gridApi.current = params.api;
    };

    const defaultColDef = {
        sortable: true,
        resizable: true,
        filter: true,
        editable: false,
        flex: 1,
        minWidth: 120,
    };

    // Cell renderer para mostrar de qué archivo viene cada dato
    const origenCellRenderer = (params) => {
        const origen = params.data.origen;
        return (
            <div>
                {origen.validacion && <Badge color="info" className="me-1">Val</Badge>}
                {origen.facturacion && <Badge color="success">Fact</Badge>}
            </div>
        );
    };

    const columnDefs = [
        {
            field: "numeroCaso",
            headerName: "Número Caso",
            checkboxSelection: true,
            headerCheckboxSelection: true,
            pinned: 'left',
        },
        {
            field: "numeroValidacion",
            headerName: "Número de Validación",
            cellStyle: params => params.value ? {} : { backgroundColor: '#ffebee' }
        },
        {
            field: "numeroFactura",
            headerName: "Número de Factura",
            cellStyle: params => params.value ? {} : { backgroundColor: '#ffebee' }
        },
        {
            field: "fechaFacturacion",
            headerName: "Fecha de Facturación",
            cellStyle: params => params.value ? {} : { backgroundColor: '#ffebee' }
        },
        {
            field: "origen",
            headerName: "Origen",
            cellRenderer: origenCellRenderer,
            maxWidth: 120,
            filter: false,
        }
    ];

    return (
        <>
            <Helmet>
                <title>Cargar Información</title>
            </Helmet>
            <div style={{ position: "relative" }}>
                {loading && <Spinner />}

                <div>
                    <BreadCrum etiquetas={etiquetas} />
                </div>

                {alert.show && (
                    <div className="container-fluid mt-3">
                        <Alert color={alert.type}>
                            {alert.message}
                        </Alert>
                    </div>
                )}

                <div style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
                    <div className="container-fluid mt-3">

                        {/* Sección de carga de archivos */}
                        <div className="card mb-3">
                            <div className="card-body">
                                <h5 className="card-title">Cargar archivos Excel</h5>

                                {/* Archivo 1: Validación */}
                                <div className="row mb-3">
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold">
                                            <i className="bi bi-file-earmark-excel"></i> Archivo 1: Número Caso + Número Validación
                                            {archivosCargados.validacion && (
                                                <Badge color="success" className="ms-2">Cargado ({dataValidacion.length})</Badge>
                                            )}
                                        </label>
                                        <input
                                            type="file"
                                            ref={fileValidacionRef}
                                            className="form-control"
                                            accept=".xlsx, .xls"
                                            onChange={handleFileValidacion}
                                            disabled={loading}
                                        />
                                        <small className="form-text text-muted">
                                            Columnas esperadas: [Número Caso, Número Validación]
                                        </small>
                                    </div>
                                </div>

                                {/* Archivo 2: Facturación */}
                                <div className="row mb-3">
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold">
                                            <i className="bi bi-file-earmark-excel"></i> Archivo 2: Número Caso + Factura + Fecha
                                            {archivosCargados.facturacion && (
                                                <Badge color="success" className="ms-2">Cargado ({dataFacturacion.length})</Badge>
                                            )}
                                        </label>
                                        <input
                                            type="file"
                                            ref={fileFacturacionRef}
                                            className="form-control"
                                            accept=".xlsx, .xls"
                                            onChange={handleFileFacturacion}
                                            disabled={loading}
                                        />
                                        <small className="form-text text-muted">
                                            Columnas esperadas: [Número Caso, Número Factura, Fecha Facturación]
                                        </small>
                                    </div>
                                </div>

                                {/* Botones de acción */}
                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="d-flex gap-2 flex-wrap">
                                            <Button
                                                color="primary"
                                                onClick={handleGuardarDatos}
                                                disabled={!hasChanges || dataCombinada.length === 0 || loading}
                                            >
                                                <i className="bi bi-save"></i> Guardar Datos ({dataCombinada.length})
                                            </Button>
                                            <Button
                                                color="warning"
                                                onClick={handleLimpiarDatos}
                                                disabled={!hasChanges || loading}
                                            >
                                                <i className="bi bi-x-circle"></i> Limpiar Todo
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Información de registros */}
                        {dataCombinada.length > 0 && (
                            <div className="alert alert-info">
                                <div className="row">
                                    <div className="col-md-3">
                                        <strong>Total registros:</strong> {dataCombinada.length}
                                    </div>
                                    <div className="col-md-3">
                                        <strong>Con validación:</strong> {dataValidacion.length}
                                    </div>
                                    <div className="col-md-3">
                                        <strong>Con facturación:</strong> {dataFacturacion.length}
                                    </div>
                                    <div className="col-md-3">
                                        {hasChanges && <Badge color="warning">Sin guardar</Badge>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tabla AG-Grid */}
                        <div className="mt-3" style={{ height: "500px", width: "100%" }}>
                            <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
                                <AgGridReact
                                    rowData={dataCombinada}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    pagination={true}
                                    onGridReady={onGridReady}
                                    localeText={localeEs}
                                    paginationPageSize={20}
                                    rowSelection="multiple"
                                    suppressRowClickSelection={true}
                                />
                            </div>
                        </div>

                        {/* Leyenda */}
                        {dataCombinada.length > 0 && (
                            <div className="mt-2">
                                <small className="text-muted">
                                    <Badge color="info">Val</Badge> = Datos del archivo de validación |
                                    <Badge color="success" className="ms-1">Fact</Badge> = Datos del archivo de facturación |
                                    <span className="ms-1" style={{ backgroundColor: '#ffebee', padding: '2px 6px' }}>Rojo</span> = Dato faltante
                                </small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default withTokenCheck(AppCargaInformacion);
